import axios from 'axios'
import useDebounce from 'src/hooks/useDebounce.js'
import React, { useEffect, useRef, useState, useContext } from 'react'
import styles from './autocomplete.module.scss'
import { IAddress, IState } from 'src/Interface.js'
import { StateContext } from '../../AppContext'
import { TweenLite } from 'gsap/all'
import { baseApiURL } from 'src/utils/utils'
import { toast } from 'react-toastify'
import { point } from 'leaflet'


interface IProps {
  label?: string
  modifiers?: string
  readOnly?: boolean
  resetError?: any
  setAddress: any
  error?: boolean
  predefinedAddresses?: Array<IAddress>
  iconType?: "destination" | "pickup";
  completedAddress?: IAddress
  name: string
  icon?: any
  placeholder?: string
  clearInputValue?: boolean
}

const AutocompletePoi = React.memo(function Autocomplete({ clearInputValue, setAddress, predefinedAddresses = [], ...props }: IProps) {
  const [inputValue, setInputValue] = useState('')
  const inputRef: any = useRef(null)
  const listRef = useRef(null)
  const container = useRef<HTMLInputElement>(null)
  const debouncedValue = useDebounce(inputValue, 300)
  const [suggestions, setSuggestions] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [focused, setFocused] = useState(false)
  const [completed, setCompleted] = useState(false)
  const state: IState = useContext(StateContext)

  useEffect(() => {
    if (props.completedAddress) {
      setInputValue(props.completedAddress.fullAddress)
      setCompleted(true)
    } else if (props.completedAddress === null) {
      setInputValue('')
      setCompleted(false)
    }
  }, [props.completedAddress])

  const clickedOutSide = (e: any) => {
    if (container.current && !container.current.contains(e.target)) {
      setFocused(false)
    }
  }

  useEffect(() => {
    window.addEventListener('click', clickedOutSide)
    return () => window.removeEventListener('click', clickedOutSide)
  }, [])

  useEffect(() => {
    if (debouncedValue.length > 2 && inputValue.length > 2 && !completed) {
      axios(`${baseApiURL()}/map_box/place?value=${debouncedValue}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result: any) => {
        setSuggestions(result.data.features)
      }).catch((e) => {
        toast(e.response.data.message)
      })
    } else {
      setSuggestions([])
    }
  }, [debouncedValue, completed, state.user.accessToken])

  const handleChange = (e: any) => {
    setCompleted(false)
    setFocused(true)
    setInputValue(e.target.value)
  }

  function handleKeyDown(e) {
    let newSelectedIndex;
    const upArrowEvent = e.keyCode === 38 && selectedIndex > 0;
    const downArrowEvent = e.keyCode === 40 && (selectedIndex < suggestions.length - 1 || selectedIndex < suggestions.length - 1);

    if (upArrowEvent) {
      newSelectedIndex = selectedIndex - 1;
    }

    if (downArrowEvent) {
      newSelectedIndex = selectedIndex + 1;
    }

    if (downArrowEvent || upArrowEvent) {
      setSelectedIndex(newSelectedIndex);
      if (newSelectedIndex === suggestions.length - 1) {
        // TweenLite.to(listRef.current, .4, { scrollTo: { y: '#active' } });
      } else {
        // TweenLite.to(listRef.current, .4, { scrollTo: { y: '#active', offsetY: 30 } });
      }
    }
  }

  const handlePredefinedAddressClick = (address: IAddress) => {
    setTimeout(function () {
      setFocused(false)
      setCompleted(true)
    }, 0);

    setAddress()({ ...address })
  }

  const handleClick = (suggestion: any) => {

    setInputValue(suggestion.place_name_da)
    const deconstructedAddress = suggestion.place_name_da.split(',')
    const isPoi = suggestion.place_type[0] === 'poi'
    const isAddress = suggestion.properties.accuracy === 'proximate' || suggestion.properties.accuracy === 'rooftop' || suggestion.properties.accuracy === 'point' || suggestion.properties.accuracy === 'interpolated'


    if (isPoi || isAddress) {
      // For POI results, only apply an offset if place_name_da actually starts with the POI name.
      // Some POI responses include the name as a prefix (e.g. "Rox Resort, Nordstranden 10, ..."),
      // while others only contain the street address (e.g. "Nordstranden 10, 4600 Køge, Danmark").
      const poiNameInAddress = isPoi && deconstructedAddress[0].trim() === suggestion.text_da
      const offset = poiNameInAddress ? 1 : 0
      const houseNo = deconstructedAddress[offset].match(/\d+/g)
      const city = deconstructedAddress[offset + 1].match(/[A-Za-zåæøøÆØÅé]+/g)
      const streetName = deconstructedAddress[offset].match(/(?! )\D+/gm)
      const zipCode = suggestion.place_name_da.match(/\d{4}/g)
      const houseLetter = suggestion.place_name_da.match(/\d+[a-zA-ZåæøÅÆØ]{1,2}/) ? suggestion.place_name_da.match(/\d+[a-zA-ZåæøÅÆØ]{1,2}/)[0].match(/[a-zA-ZåæøÅÆØ]/) : null

      setTimeout(function () {
        setFocused(false)
        setCompleted(true)
      }, 0);

      setAddress()({
        fullAddress: suggestion.place_name_da,
        houseNo: houseNo && houseNo[0] ? houseNo[0] : 9999,
        poi: isPoi ? suggestion.text_da : null,
        label: suggestion.label,
        houseLetter: houseLetter ? houseLetter[0] : null,
        city: city ? city[0] : null,
        streetName: streetName ? streetName[0].trim() : null,
        zipCode: zipCode ? zipCode[0] : null,
        longitude: suggestion.geometry.coordinates[0],
        latitude: suggestion.geometry.coordinates[1],
        placeType: suggestion.place_type[0],
        accuracy: suggestion.properties.accuracy

      })
    }
  }

  const handleFocus = () => {
    setFocused(!focused)
    if (props.resetError) {
      props.resetError()
    }
  }


  return (
    <div className={`${styles.inputField} ${props.modifiers}`} ref={container}>
      {props.label && <label htmlFor={props.name} className={styles.inputLabel}>{props.label}</label>}
      <div className={`${styles.inputWrapper} ${props.error ? styles.errorState : ''} ${focused ? styles.focused : ''} ${completed ? styles.completed : ''}`}>
        {props.icon && <span className={`${styles.inputIcon} ${completed ? styles.iconGreen : ''}`}>{props.icon}</span>}
        <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleClick(suggestions[selectedIndex]); }}>
          <input
            id={props.name}
            className={`${styles.input} ${props.iconType === 'destination' ? styles.destinationIcon : ''} ${props.iconType === 'pickup' ? styles.pickupIcon : ''}`}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            value={inputValue}
            ref={inputRef}
            onKeyDown={(e) => handleKeyDown(e)}
            onChange={(e) => handleChange(e)}
            onFocus={handleFocus}
            autoComplete="off"
          />
        </form>
        <div onClick={handleFocus} className={styles.indicatorsContainer}>
          <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
        {(debouncedValue.length >= 3 || predefinedAddresses.length > 0) && focused && !props.readOnly && <ul className={styles.list} ref={listRef}>
          {debouncedValue.length >= 3 && suggestions?.length > 0 ?
            suggestions.map((suggestion, index) => {
              return (
                <li
                  key={index}
                  onClick={() => handleClick(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  id={selectedIndex === index ? 'active' : ''}
                  onMouseLeave={() => setSelectedIndex(-1)}
                  className={`${styles.item} ${selectedIndex === index ? styles.active : ''}`}
                >
                  <>
                    <span className={styles.itemText}>{suggestion.label ? suggestion.label : suggestion.text_da}</span>
                    <span className={`${styles.itemText} opacity-70`}>{suggestion.place_name_da.replace(`${suggestion.text_da},`, '')}</span>
                  </>
                </li>
              )
            }) :
            predefinedAddresses?.length > 0 ? <></> :
              <li className={styles.item}>Ingen resultater fundet</li>
          }
          {
            predefinedAddresses?.length > 0 ?
              <>
                <li className={`${styles.itemText} ${styles.groupHeader}`}>Favorit Addresses</li>
                {predefinedAddresses?.map((suggestion, index) => {
                  return (
                    <li
                      key={index}
                      onClick={() => handlePredefinedAddressClick(suggestion)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      id={selectedIndex === index ? 'active' : ''}
                      onMouseLeave={() => setSelectedIndex(-1)}
                      className={`${styles.item} ${selectedIndex === index ? styles.active : ''}`}
                    >
                      <>
                        <span className={styles.itemText}>{suggestion.streetName}
                          <svg data-title='address' xmlns="http://www.w3.org/2000/svg" className={`${styles.textStar} tw-h-5 tw-w-5 tw-mr-2`} viewBox="0 0 20 20" fill="currentColor">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg> </span>
                        <span className={`${styles.itemText} opacity-70`}>{suggestion.fullAddress.replace(`${suggestion.streetName},`, '')}</span>
                      </>
                    </li>
                  )
                })}
              </> : <></>
          }
        </ul>}
      </div>
    </div >
  )
})

export default AutocompletePoi
